import React, { FC, memo } from 'react';
import { useProject } from '@context/Project/ProjectProvider';
import { Box, Divider, Stack } from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { AdditionalSettings } from '@views/Project/components/AdditionalSettings/AdditionalSettings';
import ModelFiltersPopover from '@views/Project/components/ModelFiltersPopover';
import AppliedFilterChips from '@views/Project/components/Toolbox/components/AppliedFilterChips';
import DashboardFilters from '@views/Project/components/Toolbox/components/DashboardFilters';
import useDashboardPartnerFilter from '@views/Project/components/Toolbox/components/DashboardFilters/useDashboardPartnerFilter';
import DashboardPartnerChips from '@views/Project/components/Toolbox/components/DashboardPartnerChips';
import LinkButton from '@views/Project/components/Toolbox/components/LinkButton';
import ModesDropdown from '@views/Project/components/Toolbox/components/ModesDropdown';
import useFilterAvailability from '@views/Project/components/Toolbox/hooks/useFilterAvailability';
import useToolboxLinks from '@views/Project/components/Toolbox/hooks/useToolboxLinks';
import { LinksWrapper } from '@views/Project/components/Toolbox/Toolbox.styled';

/**
 * The project toolbox row: mode selector, the view tabs, the Filters funnel
 * with the applied-filter chips beside it, then the kebab pinned to the far
 * right. The Scenarios toggle belongs to the mode header above, not to this
 * row. In Dashboard view the funnel and chip slots hold the dashboard's
 * partner funnel and partner chips instead of the model ones. The left group
 * takes the leftover width and may shrink while the kebab never does, so the
 * chip row's container measures exactly the space between the funnel and the
 * kebab and overflows into +N instead of growing the toolbar. The row's own
 * gap is what keeps the last chip off the kebab: the left group grows to
 * fill, so without it a full chip row would sit flush against the kebab.
 *
 * Whether the funnel is shown at all, and whether it is locked, is one
 * decision per mode and view made by `useFilterAvailability`. On the
 * Dashboard it is narrowed further by the partner filter's own availability
 * — a tab that supports partners and a project that has some — so the
 * separator never stands alone in front of a funnel that renders nothing.
 * Hidden takes its separator and the chips with it, so the row then reads
 * mode selector, tabs, spacer, kebab; locked keeps everything in place and
 * hands the lock to the funnel and the chips.
 */
const Toolbox: FC = () => {
  const { isVersionsShown, isDashboardMode } = useProject();
  const availability = useFilterAvailability();
  const { isAvailable: isDashboardFilterAvailable } =
    useDashboardPartnerFilter();
  const isProjectLinksDisabled = isVersionsShown;
  const areFiltersShown =
    availability !== 'hidden' &&
    (!isDashboardMode || isDashboardFilterAvailable);
  const areFiltersLocked = availability === 'locked';

  const { toolboxLinks, handleLinkClick } = useToolboxLinks();

  return (
    <Stack
      p={6}
      gap={4}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      borderBottom={`1px solid ${themePalette.grey[500]}`}
    >
      <Stack gap={4} direction="row" alignItems="center" flex={1} minWidth={0}>
        <ModesDropdown />

        <Divider orientation="vertical" flexItem />

        <LinksWrapper $disabled={isProjectLinksDisabled}>
          {toolboxLinks.map((linkItem) => (
            <LinkButton
              key={linkItem.label}
              item={linkItem}
              onClick={(e) => handleLinkClick(e, linkItem.to)}
            />
          ))}
        </LinksWrapper>

        {areFiltersShown && (
          <>
            <Divider orientation="vertical" flexItem />

            {isDashboardMode ? (
              <DashboardFilters />
            ) : (
              <ModelFiltersPopover isLocked={areFiltersLocked} />
            )}

            {isDashboardMode ? (
              <DashboardPartnerChips />
            ) : (
              <AppliedFilterChips isLocked={areFiltersLocked} />
            )}
          </>
        )}
      </Stack>

      <Box flexShrink={0}>
        <AdditionalSettings />
      </Box>
    </Stack>
  );
};

export default memo(Toolbox);
